
import { useState, useCallback } from "react";
import { CalendarEvent } from "@/hooks/calendar/types";
import { TimeSlot } from "./types";
import { useToast } from "@/hooks/use-toast";

export const useTimeSlotHandlers = (
  selectedTimeSlot: TimeSlot | null,
  setSelectedTimeSlot: (slot: TimeSlot | null) => void,
  setIsBookingDialogOpen: (open: boolean) => void,
  setIsClientRequestDialogOpen: (open: boolean) => void,
  setIsConflictDialogOpen: (open: boolean) => void,
  setIsPastTimeSlotDialogOpen: (open: boolean) => void,
  setDeleteDialogOpen: (open: boolean) => void,
  setEventToDelete: (event: CalendarEvent | null) => void,
  setConflictingEvents: (events: CalendarEvent[]) => void,
  isClientView: boolean,
  clientId: string | null,
  getEventsForDateAndHour: (date: Date, hour: number, minute?: number) => CalendarEvent[],
  onAddEvent?: (event: Omit<CalendarEvent, "id">) => Promise<void>,
  onDeleteEvent?: (event: CalendarEvent) => Promise<void>
) => {
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  // Aggiungiamo una variabile di stato interna per tenere traccia dell'evento da eliminare
  const [eventToDeleteLocal, setEventToDeleteLocal] = useState<CalendarEvent | null>(null);

  // Funzione per gestire la conferma di una prenotazione in uno slot passato
  const handlePastTimeSlotConfirm = useCallback(() => {
    setIsPastTimeSlotDialogOpen(false);
    
    if (selectedTimeSlot) {
      setIsBookingDialogOpen(true);
    }
  }, [selectedTimeSlot, setIsPastTimeSlotDialogOpen, setIsBookingDialogOpen]);

  // Funzione per gestire l'annullamento di una prenotazione in uno slot passato
  const handlePastTimeSlotCancel = useCallback(() => {
    setIsPastTimeSlotDialogOpen(false);
    setSelectedTimeSlot(null);
  }, [setIsPastTimeSlotDialogOpen, setSelectedTimeSlot]);

  // Funzione per gestire la conferma di una prenotazione con conflitti
  const handleConflictConfirm = useCallback(() => {
    setIsConflictDialogOpen(false);
    
    if (selectedTimeSlot) {
      if (isClientView) {
        setIsClientRequestDialogOpen(true);
      } else {
        setIsBookingDialogOpen(true);
      }
    }
  }, [selectedTimeSlot, isClientView, setIsConflictDialogOpen, setIsClientRequestDialogOpen, setIsBookingDialogOpen]);

  // Funzione per gestire l'annullamento di una prenotazione con conflitti
  const handleConflictCancel = useCallback(() => {
    setIsConflictDialogOpen(false);
    setSelectedTimeSlot(null);
    setConflictingEvents([]);
  }, [setIsConflictDialogOpen, setSelectedTimeSlot, setConflictingEvents]);

  // Funzione per gestire l'eliminazione di un evento
  const handleDeleteEvent = useCallback(async (event: CalendarEvent): Promise<void> => {
    console.log("handleDeleteEvent chiamato con evento:", event);
    setEventToDeleteLocal(event);
    setEventToDelete(event);
    setDeleteDialogOpen(true);
    
    // Ritorniamo una Promise risolta per mantenere la compatibilità di tipo
    return Promise.resolve();
  }, [setEventToDelete, setDeleteDialogOpen]);

  // Funzione per gestire l'annullamento dell'eliminazione di un evento
  const handleDeleteCancel = useCallback(() => {
    setEventToDeleteLocal(null);
    setEventToDelete(null);
    setDeleteDialogOpen(false);
  }, [setEventToDelete, setDeleteDialogOpen]);

  // Funzione per gestire la conferma dell'eliminazione di un evento
  const handleDeleteConfirm = useCallback(async () => {
    // Utilizziamo lo stato locale invece di cercare di estrarre il valore corrente
    if (!onDeleteEvent || !eventToDeleteLocal) {
      console.log("onDeleteEvent non disponibile o evento da eliminare mancante");
      return;
    }
    
    try {
      console.log("Inizio eliminazione evento:", eventToDeleteLocal.id);
      
      // Chiamata effettiva per eliminare l'evento
      await onDeleteEvent(eventToDeleteLocal);
      
      console.log("Evento eliminato con successo:", eventToDeleteLocal.id);
      setDeleteDialogOpen(false);
      setEventToDeleteLocal(null);
      setEventToDelete(null);
    } catch (error) {
      console.error("Errore durante l'eliminazione dell'evento:", error);
      toast({
        title: "Errore",
        description: error instanceof Error ? error.message : "Impossibile eliminare la sessione. Riprova più tardi.",
        variant: "destructive"
      });
    }
  }, [onDeleteEvent, eventToDeleteLocal, setDeleteDialogOpen, setEventToDelete, toast]);

  // Funzione per gestire la chiusura del dialogo di prenotazione
  const handleBookingClose = useCallback(() => {
    setIsBookingDialogOpen(false);
    setSelectedTimeSlot(null);
  }, [setIsBookingDialogOpen, setSelectedTimeSlot]);

  // Funzione per gestire la conferma di una prenotazione
  const handleBookingConfirm = useCallback(async (clientId: string, packageId: string, title: string, remainingHours: number): Promise<void> => {
    if (!onAddEvent || !selectedTimeSlot) return;
    
    try {
      const eventData: Omit<CalendarEvent, "id"> = {
        title,
        date: selectedTimeSlot.date,
        hour: selectedTimeSlot.hour,
        minute: 0,
        clientId,
        remainingHours,
        type: "session"
      };
      
      await onAddEvent(eventData);
      setIsBookingDialogOpen(false);
      setSelectedTimeSlot(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Errore durante la prenotazione"));
      console.error("Errore durante la conferma della prenotazione:", err);
    }
  }, [onAddEvent, selectedTimeSlot, setIsBookingDialogOpen, setSelectedTimeSlot]);

  // Funzione per gestire la conferma di una richiesta di sessione da parte di un cliente
  const handleClientRequestConfirm = useCallback(async (packageId: string, remainingHours: number): Promise<void> => {
    if (!onAddEvent || !selectedTimeSlot || !clientId) return;
    
    try {
      const eventData: Omit<CalendarEvent, "id"> = {
        title: "Richiesta sessione",
        date: selectedTimeSlot.date,
        hour: selectedTimeSlot.hour,
        minute: 0,
        clientId,
        remainingHours,
        type: "session"
      };
      
      await onAddEvent(eventData);
      setIsClientRequestDialogOpen(false);
      setSelectedTimeSlot(null);
      
      toast({
        title: "Richiesta inviata",
        description: "La tua richiesta di sessione è stata inviata e sarà esaminata dall'amministratore",
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Errore durante la richiesta della sessione"));
      console.error("Errore durante la richiesta della sessione:", err);
    }
  }, [onAddEvent, selectedTimeSlot, clientId, setIsClientRequestDialogOpen, setSelectedTimeSlot, toast]);

  // Ritorna tutte le funzioni di gestione
  return {
    handlePastTimeSlotConfirm,
    handlePastTimeSlotCancel,
    handleConflictConfirm,
    handleConflictCancel,
    handleDeleteEvent,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleBookingClose,
    handleBookingConfirm,
    handleClientRequestConfirm,
    error
  };
};
